import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { NetworkStatsData, Block, TransactionData } from '../types/dashboard';

const REFETCH_INTERVAL_MS = 1 * 60 * 1000; // Check every 1 minute
const MAX_DATA_AGE_MS = 3 * 60 * 1000;   // Refetch if data is older than 3 minutes

// Interface for data coming from Supabase 'dashboard_metrics' table (snake_case)
interface DbDashboardMetrics {
  id?: number;
  price_usd: number | null;
  price_change_percent_24h: number | null;
  exchange_volume_24h_asset: string | null;
  exchange_volume_24h_value: number | null;
  market_cap_usd: number | null;
  hashrate: string | null; // Current hashrate (already in network_summary_stats, but also stored here for consistency by edge fn)
  hashrate_change_24h_percent: number | null;
  active_nodes: number | null; // Current active nodes (already in network_summary_stats)
  active_nodes_change_24h: number | null;
  blocks_last_hour: number | null; // Still deferred on backend
  transaction_volume_24h_coins: number | null; // On-chain, still deferred on backend
  average_transaction_fee_24h_coins: number | null; // On-chain, still deferred on backend
  last_updated_at?: string | null;
}

// Helper to transform DB data to the NetworkStatsData format used by components
// Also incorporates some data that might now come from networkSummary (via useNetwork hook if preferred elsewhere)
// but for dashboard-specific views, it's cleaner to get it from dashboard_metrics if Edge Fn populates it.
const transformDbToNetworkStatsData = (
  dbMetrics: DbDashboardMetrics | null,
  // networkSummary?: OriginalNetworkSummary // Optional: if we needed to merge data from useNetwork()
): NetworkStatsData => {
  // Fallback structure, similar to original mock but will be overridden
  const fallbackStats: NetworkStatsData = {
    hashrate: 'N/A',
    hashrateChange: 'N/A',
    blockHeight: 0, // This should ideally come from useNetwork() if dashboard needs latest block height
    blocksLastHour: 0, // Mocked/Deferred
    activeNodes: 0,
    nodeChange: 'N/A',
    marketCap: 0,
    marketCapChange: 'N/A',
  };

  if (!dbMetrics) {
    return fallbackStats;
  }

  // Map from dbMetrics to NetworkStatsData
  // Note: blockHeight is NOT in dashboard_metrics, it comes from network_summary_stats (via useNetwork hook)
  // The dashboard component will need to combine data from useNetwork() and useDashboardData() or we pass networkSummary here.
  // For now, this hook will primarily provide what's in dashboard_metrics.
  return {
    hashrate: dbMetrics.hashrate ?? fallbackStats.hashrate,
    hashrateChange: dbMetrics.hashrate_change_24h_percent !== null ? `${dbMetrics.hashrate_change_24h_percent.toFixed(2)}%` : fallbackStats.hashrateChange,
    blockHeight: fallbackStats.blockHeight, // Placeholder - Dashboard should get this from useNetwork()
    blocksLastHour: dbMetrics.blocks_last_hour ?? fallbackStats.blocksLastHour, // Still deferred
    activeNodes: dbMetrics.active_nodes ?? fallbackStats.activeNodes,
    nodeChange: dbMetrics.active_nodes_change_24h !== null ? `${dbMetrics.active_nodes_change_24h > 0 ? '+' : ''}${dbMetrics.active_nodes_change_24h.toFixed(0)}` : fallbackStats.nodeChange,
    marketCap: dbMetrics.market_cap_usd ?? fallbackStats.marketCap,
    marketCapChange: dbMetrics.price_change_percent_24h !== null ? `${dbMetrics.price_change_percent_24h.toFixed(2)}%` : fallbackStats.marketCapChange, // Using price_change_percent_24h as proxy for marketCapChange
  };
};

export function useDashboardData() {
  // Initial state matching the hook's return structure
  const [stats, setStats] = useState<NetworkStatsData>(() => transformDbToNetworkStatsData(null));
  const [blocks, setBlocks] = useState<Block[]>([]); // Still deferred, return empty
  const [transactions, setTransactions] = useState<TransactionData>({ // Partially from exchange, rest deferred
    volume24h: 0, // Will be populated by exchange_volume_24h_value if available
    avgFee: 0, // On-chain, deferred
    history: [], // On-chain transaction history for chart, deferred
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTimestamp, setLastFetchTimestamp] = useState<number>(0);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (!isInitialLoad) {
      console.log('[useDashboardData] Background refetch triggered...');
    }
    setError(null);
    try {
      const { data: dbData, error: dbError } = await supabase
        .from('dashboard_metrics')
        .select('*')
        .eq('id', 1)
        .single<DbDashboardMetrics>();

      if (dbError) {
        console.error('[useDashboardData] Error fetching dashboard metrics from Supabase:', dbError);
        if (isInitialLoad) {
            setStats(transformDbToNetworkStatsData(null));
            setTransactions(prev => ({ ...prev, volume24h: 0 }));
        }
        setError(new Error('Failed to fetch dashboard data: ' + dbError.message));
      } else if (dbData) {
        setStats(transformDbToNetworkStatsData(dbData));
        setTransactions(prev => ({
          ...prev,
          volume24h: dbData.exchange_volume_24h_value ?? 0,
        }));
        setLastFetchTimestamp(Date.now());
        console.log('[useDashboardData] Fetched dashboard metrics');
      } else {
        if (isInitialLoad) {
            setStats(transformDbToNetworkStatsData(null));
            setTransactions(prev => ({ ...prev, volume24h: 0 }));
        }
        setError(new Error('No dashboard metrics data found in database.'));
      }
    } catch (err) {
      console.error('[useDashboardData] Catch block error fetching dashboard data:', err);
      if (isInitialLoad) {
        setStats(transformDbToNetworkStatsData(null));
        setTransactions(prev => ({ ...prev, volume24h: 0 }));
      }
      setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  }, []);

  // Initial fetch and real-time subscription
  useEffect(() => {
    let mounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupSubscription = async () => {
      // Initial fetch
      setLoading(true);
      await fetchData(true);

      if (!mounted) return;

      // Set up real-time subscription
      channel = supabase
        .channel('public:dashboard_metrics')
        .on(
          'postgres_changes',
          { 
            event: '*', 
            schema: 'public', 
            table: 'dashboard_metrics',
            filter: 'id=eq.1' // Only listen for updates to the single row
          },
          (payload) => {
            if (!mounted) return;
            console.log('[useDashboardData] Realtime update received:', payload);
            
            // Handle different event types
            if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
              const newData = payload.new as DbDashboardMetrics;
              if (newData) {
                setStats(transformDbToNetworkStatsData(newData));
                setTransactions(prev => ({
                  ...prev,
                  volume24h: newData.exchange_volume_24h_value ?? 0,
                }));
                setLastFetchTimestamp(Date.now());
                console.log('[useDashboardData] Dashboard data updated via realtime');
              }
            }
          }
        )
        .subscribe((status, err) => {
          if (!mounted) return;
          if (err) {
            console.error('[useDashboardData] Realtime subscription error:', err);
          } else if (status === 'SUBSCRIBED') {
            console.log('[useDashboardData] Successfully subscribed to dashboard_metrics updates');
          }
        });
    };

    setupSubscription();

    return () => {
      mounted = false;
      if (channel) {
        // Use timeout to ensure cleanup happens after any pending operations
        const channelToRemove = channel;
        setTimeout(() => {
          supabase.removeChannel(channelToRemove).catch(err => 
            console.error('[useDashboardData] Failed to remove channel:', err)
          );
        }, 0);
      }
    };
  }, [fetchData]);

  // Periodic refetch if data is stale and tab is visible (as fallback)
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[useDashboardData] Data is stale, refetching due to interval...');
          fetchData();
        }
      }
    }, REFETCH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [lastFetchTimestamp, fetchData]);

  // Refetch on window focus if data is stale (as fallback)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[useDashboardData] Tab focused and data is stale, refetching...');
          fetchData();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lastFetchTimestamp, fetchData]);

  // The hook returns data structured as the components expect it
  return { stats, blocks, transactions, loading, error };
}